/**
 * One shared, random-access endurance course. The browser and score verifier stream the same
 * bounded geometry and progress counters; neither stores an ever-growing course or input tape.
 * Generation and rebasing happen between ticks. Ordinary movement physics remain unchanged.
 */
import { rebasePracticeSim } from "./rebase";
import { Sim } from "./runner";
import type { SimState } from "./state";
import { defaultTuning, type Tuning } from "./tuning";
import { type CourseData, type CourseEntity, copyInputFrame, type InputFrame, S } from "./types";

export const BURNOUT_COURSE = "burnout";
export const BURNOUT_TITLE = "Burnout";
export const BURNOUT_VERSION = 3;
export const BURNOUT_CHUNK_LENGTH = 96;
export const BURNOUT_WINDOW_CHUNKS = 5;

export type BurnoutSectionKind = "gap" | "lane" | "vault" | "slide" | "hurdle" | "ladder";
export interface BurnoutSection {
  /** Absolute chunk and one-based obstacle number, never an index into a retained array. */
  index: number;
  ordinal: number;
  kind: BurnoutSectionKind;
  tier: number;
  /** Coordinates are local to this generated window. */
  x: number;
  endX: number;
  clearX: number;
  /** Ladders count only after a safe landing on their high receiving platform. */
  clearY?: number;
}
export interface BurnoutWindow {
  course: CourseData;
  firstChunk: number;
  originX: number;
  endX: number;
  sections: BurnoutSection[];
}

/** Preserve the familiar opening, then add a modest density increase at proven dimensions. */
export function burnoutTier(chunk: number): number {
  return chunk === 0 ? 0 : chunk === 1 ? 2 : 3;
}
function sectionsBefore(chunk: number): number {
  if (chunk === 0) return 0;
  if (chunk === 1) return 8;
  const later = chunk - 2;
  return 17 + Math.floor(later / 2) * 21 + (later % 2) * 10;
}
/** Both halves participate: indices beyond 2^32 do not restart the opening sequence. */
function sectionSeed(ordinal: number): number {
  let value = 0x4255524e ^ Math.imul(ordinal >>> 0, 0x9e3779b1);
  value ^= Math.imul(Math.floor(ordinal / 0x100000000), 0x85ebca6b);
  value = Math.imul(value ^ (value >>> 16), 0x7feb352d);
  return (value ^ (value >>> 15)) >>> 0;
}
const OPENING_KINDS: readonly BurnoutSectionKind[] = ["gap", "vault", "slide", "lane", "hurdle"];

function addDryObstacle(entities: CourseEntity[], kind: BurnoutSectionKind, x: number, tier: number): number {
  if (kind === "slide") {
    const width = tier === 3 ? 2.5 : 2;
    entities.push({ type: "pipe", x, y: 1.5, w: width, h: 0.4 });
    return width;
  }
  const width = kind === "hurdle" ? 0.75 : 1;
  const height = kind === "hurdle" ? 0.4 : tier === 0 ? 1 : 1.2;
  entities.push({ type: "box", x, y: 0, w: width, h: height });
  return width;
}

export function generateBurnoutWindow(firstChunk = 0): BurnoutWindow {
  if (
    !Number.isSafeInteger(firstChunk) ||
    firstChunk < 0 ||
    !Number.isSafeInteger((firstChunk + BURNOUT_WINDOW_CHUNKS) * BURNOUT_CHUNK_LENGTH)
  ) {
    throw new RangeError("Burnout chunk index must be a nonnegative safe integer");
  }
  const entities: CourseEntity[] = [];
  const sections: BurnoutSection[] = [];
  for (let chunk = 0; chunk < BURNOUT_WINDOW_CHUNKS; chunk++) {
    const index = firstChunk + chunk;
    const tier = burnoutTier(index);
    const cells = 6;
    const width = BURNOUT_CHUNK_LENGTH / cells;
    const hasLadder = index >= 4 && index % 4 === 0;
    const rotation = sectionSeed(index + 1) % 6;
    // Occasional triples follow another dry cell, never the flight out of a bar lane.
    const tripleSlot = hasLadder
      ? 5
      : index >= 3 && index % 2 === 1
        ? [1, 2, 3, 4, 5].find((slot) => (slot + rotation) % 3 === 1)!
        : -1;
    let ordinal = sectionsBefore(index) + 1;
    for (let slot = 0; slot < cells; slot++) {
      const x = chunk * BURNOUT_CHUNK_LENGTH + slot * width;
      if (hasLadder && slot === 2) {
        const rungs = 5;
        const top = 3.6 + rungs - 1 - 0.4;
        entities.push(
          { type: "solid", x, y: -1, w: 4, h: 1 },
          { type: "water", x: x + 4, y: -2, w: 12, h: 1 },
          { type: "ladder", x: x + 8, y: 3.6, rungs, spacing: 1 },
          { type: "box", x: x + 10.5, y: -2, w: 5.5, h: top + 2 },
          { type: "solid", x: x + 16, y: -1, w: 16, h: 1 },
        );
        sections.push({
          index,
          ordinal,
          kind: "ladder",
          tier,
          x,
          endX: x + 32,
          // Include near-edge contact: grounded height below proves support on the receiver.
          // Half-metre coordinates also remain bit-exact across shifted windows.
          clearX: x + 10,
          clearY: top,
        });
        ordinal++;
        slot++;
        continue;
      }
      const seed = sectionSeed(ordinal);
      const denseDry =
        index === 0
          ? slot === 0 || slot === 3
          : index === 1
            ? (slot + rotation) % 6 === 0 || (slot + rotation) % 6 === 1 || (slot + rotation) % 6 === 3
            : hasLadder || (slot + rotation) % 3 !== 2;
      const dryCount = slot === tripleSlot ? 3 : 2;
      const kind: BurnoutSectionKind =
        ordinal === 1
          ? "hurdle"
          : denseDry
            ? seed % 3 === 0
              ? "slide"
              : seed % 3 === 1
                ? "vault"
                : "hurdle"
            : tier === 0
              ? OPENING_KINDS[seed % OPENING_KINDS.length]!
              : seed % 2 === 0
                ? "gap"
                : "lane";
      const endX = x + width;
      const at = x + (denseDry && dryCount === 3 ? 3 : 5);
      let obstacleWidth = 1;
      if (kind === "gap" || kind === "lane") {
        const bars = kind === "lane" ? (tier === 0 ? 2 : 3) : 0;
        obstacleWidth = kind === "lane" ? 3 * bars - 1 : 2.625 + tier * 0.1875;
        entities.push(
          { type: "solid", x, y: -1, w: at - x, h: 1 },
          { type: "water", x: at, y: -2, w: obstacleWidth, h: 1 },
        );
        for (let bar = 0; bar < bars; bar++) {
          entities.push({ type: "bar", x: at + 3 * (bar + 1), y: 3.8 });
        }
        entities.push({ type: "solid", x: at + obstacleWidth, y: -1, w: endX - at - obstacleWidth, h: 1 });
      } else {
        entities.push({ type: "solid", x, y: -1, w: width, h: 1 });
        obstacleWidth = addDryObstacle(entities, kind, at, tier);
      }
      sections.push({
        index,
        ordinal,
        kind,
        tier,
        x,
        endX: denseDry ? at + (dryCount === 3 ? 5 : 6) : endX,
        clearX: at + obstacleWidth + 0.5,
      });
      ordinal++;
      if (denseDry) {
        for (let obstacle = 1; obstacle < dryCount; obstacle++) {
          const nextKind = obstacle % 2 === 1 ? (kind === "slide" ? "vault" : "slide") : kind;
          const nextAt = at + obstacle * (dryCount === 3 ? 5 : 6);
          const nextWidth = addDryObstacle(entities, nextKind, nextAt, tier);
          sections.push({
            index,
            ordinal,
            kind: nextKind,
            tier,
            x: nextAt,
            endX: obstacle === dryCount - 1 ? endX : nextAt + 5,
            clearX: nextAt + nextWidth + 0.5,
          });
          ordinal++;
        }
      }
    }
  }
  entities.push({ type: "solid", x: -3, y: -1, w: 3, h: 1 }, { type: "solid", x: -4, y: -1, w: 1, h: 6 });
  return {
    course: {
      version: 1,
      name: BURNOUT_COURSE,
      title: BURNOUT_TITLE,
      start: { x: 2, y: 0 },
      // No checkpoints: the first failure ends this run; a fresh run starts at obstacle one.
      respawns: [{ x: 2, y: 0 }],
      marks: [],
      entities,
    },
    firstChunk,
    originX: firstChunk * BURNOUT_CHUNK_LENGTH,
    endX: BURNOUT_WINDOW_CHUNKS * BURNOUT_CHUNK_LENGTH,
    sections,
  };
}

export interface BurnoutProgress {
  ticks: number;
  obstacles: number;
  /** Time is frozen at the latest successfully cleared obstacle, so waiting cannot improve it. */
  clearTick: number;
  failed: boolean;
}
export function createBurnoutProgress(): BurnoutProgress {
  return { ticks: 0, obstacles: 0, clearTick: 0, failed: false };
}
/** Call exactly once after each simulation tick, before replacing the window. No per-tick allocation. */
export function updateBurnoutProgress(
  progress: BurnoutProgress,
  state: SimState,
  window: BurnoutWindow,
): void {
  if (progress.failed) return;
  progress.ticks++;
  if (state.state === S.Fail) {
    progress.failed = true;
    return;
  }
  // Safe contact beyond a challenge closes it. Flying above water alone never earns a clear.
  if (state.grounded !== 1 || state.y < -0.05) return;
  for (let index = 0; index < window.sections.length; index++) {
    const section = window.sections[index]!;
    if (section.ordinal <= progress.obstacles) continue;
    // Retiring a window must never silently award challenges that were not cleared in it.
    if (section.ordinal !== progress.obstacles + 1) break;
    if (state.x < section.clearX) break;
    if (section.clearY !== undefined && state.y < section.clearY - 0.05) break;
    progress.obstacles = section.ordinal;
    progress.clearTick = progress.ticks;
  }
}
/** Retain the previous chunk behind the player and at least two full chunks ahead. */
export function burnoutNextWindow(window: BurnoutWindow, state: SimState): BurnoutWindow | null {
  if (state.state === S.Fail || state.x < 3 * BURNOUT_CHUNK_LENGTH) return null;
  return generateBurnoutWindow(window.firstChunk + 2);
}

type NumericState = Omit<SimState, "copyFrom">;
export interface BurnoutSnapshot {
  version: number;
  firstChunk: number;
  progress: BurnoutProgress;
  state: NumericState;
  prev: NumericState;
  input: InputFrame;
}
/** Server continuation and headless tests; the app uses these same helpers around its Player. */
export class BurnoutRun {
  sim: Sim;
  window: BurnoutWindow;
  readonly progress: BurnoutProgress;
  constructor(tuning: Tuning = defaultTuning, snapshot?: BurnoutSnapshot) {
    if (snapshot && snapshot.version !== BURNOUT_VERSION) throw new Error("Unsupported Burnout version");
    this.window = generateBurnoutWindow(snapshot?.firstChunk ?? 0);
    this.sim = new Sim(this.window.course, tuning);
    this.progress = snapshot ? { ...snapshot.progress } : createBurnoutProgress();
    if (snapshot) {
      // Snapshots are server-owned persisted continuations, never trusted client submissions.
      Object.assign(this.sim.state, snapshot.state);
      Object.assign(this.sim.prev, snapshot.prev);
      copyInputFrame(this.sim.input, snapshot.input);
    }
  }
  static restore(snapshot: BurnoutSnapshot, tuning: Tuning = defaultTuning): BurnoutRun {
    return new BurnoutRun(tuning, snapshot);
  }
  step(input: InputFrame): void {
    if (this.progress.failed) return;
    if (input.restart) throw new Error("A Burnout restart requires a fresh run");
    copyInputFrame(this.sim.input, input);
    this.sim.step(input);
    updateBurnoutProgress(this.progress, this.sim.state, this.window);
    const next = burnoutNextWindow(this.window, this.sim.state);
    if (next) {
      this.sim = rebasePracticeSim(this.sim, next.course, next.originX - this.window.originX);
      this.window = next;
    }
  }
  snapshot(): BurnoutSnapshot {
    return {
      version: BURNOUT_VERSION,
      firstChunk: this.window.firstChunk,
      progress: { ...this.progress },
      state: { ...this.sim.state },
      prev: { ...this.sim.prev },
      input: { ...this.sim.input },
    };
  }
}
